import React from 'react';
import { useStyletron } from 'baseui';
import { Accordion, Panel } from 'baseui/accordion';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Input } from 'baseui/input';
import { Modal, ModalBody, ModalFooter, ModalHeader, ROLE, SIZE } from 'baseui/modal';
import { Select } from 'baseui/select';
import { toaster } from 'baseui/toast';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import Router from 'next/router';
import { Button, ButtonRow } from '@gtintel/platform-atoms';
import { ROUTES } from '@gtintel/platform-constants';
import { SubjectTypes } from '@gtintel/platform-types';
import { formatDate } from '@gtintel/platform-utils';
import { Subjects } from '../ProjectApp/admin/Subjects';
import { ProjectCreateContext } from '../ProjectApp/context';
import Editor from '../components/Editor';
import { FormHeader } from '../components/Header';
const ProjectCreationForm = () => {
  const [css] = useStyletron();
  const projectCtx = React.useContext(ProjectCreateContext);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [projectCreated, setProjectCreated] = React.useState(!!projectCtx.getID());
  const [isCreating, setIsCreating] = React.useState(false);
  const [creationFailed, setCreationFailed] = React.useState(false);
  const gtiTemplateSelected = projectCtx.getTemplate(projectCtx.getSelectedTemplate())?.name.includes('GTI');
  const mapJurisdictions = jurisdictions => {
    return jurisdictions.map(jurisdiction => projectCtx.getJurisdiction(jurisdiction).name);
  };
  const template = projectCtx.getTemplate(projectCtx.getSelectedTemplate());

  // ---------------------------------------------------------------------------------------
  // Redirect to project detail if created
  // ---------------------------------------------------------------------------------------
  const [counter, setCounter] = React.useState(5);
  React.useEffect(() => {
    projectCreated && counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
  }, [counter, projectCreated]);
  if (projectCreated && counter === 0) {
    Router.push(`${ROUTES.CLIENT_PROJECT_DETAIL}/${projectCtx.getID()}`);
  }
  if (projectCreated) {
    return <section className={css({
      alignItems: 'center',
      background: '#fff',
      display: 'flex',
      flexFlow: 'column',
      margin: 'calc(50vh - 226px) auto',
      height: '200px',
      justifyContent: 'center',
      padding: '24px',
      width: '1200px',
      '@media (max-width: 1200px)': {
        maxWidth: '100%'
      }
    })}>
        <HeadingLevel>
          <Heading>Project created successfully!</Heading>
        </HeadingLevel>
        <p>
          Navigating to it in {counter} seconds. Click{' '}
          <a href={`${ROUTES.CLIENT_PROJECT_DETAIL}/${projectCtx.getID()}`}>
            here
          </a>{' '}
          to go there now
        </p>
      </section>;
  }

  // ---------------------------------------------------------------------------------------

  return <section data-testid="project-creation-form" className={css({
    background: '#fff',
    margin: '32px auto',
    display: 'flex',
    flexFlow: 'column',
    gap: '24px',
    padding: '24px',
    width: '1200px',
    '@media (max-width: 1200px)': {
      maxWidth: '100%'
    }
  })}>
      <HeadingLevel>
        <Heading>Create a new Project</Heading>
        <div className={css({
        marginBottom: '1em'
      })} data-testid="import-template-select">
          <FormHeader level={4}>Select a saved Template</FormHeader>

          <div className={gtiTemplateSelected ? css({
          backgroundColor: 'rgba(255, 186, 0, 0.1)',
          borderRadius: '4px',
          color: 'rgb(255, 186, 0)',
          padding: '14px'
        }) : css({
          padding: '14px'
        })}>
            {gtiTemplateSelected && <p>
                This is a GTI template so only <i>online</i> checks will be
                performed
              </p>}

            <Select placeholder="Select Template..." options={projectCtx.templateOptions()} labelKey="name" valueKey="id" value={[{
            id: projectCtx.getSelectedTemplate()
          }]} onChange={async ({
            value: [{
              id
            }]
          }) => {
            projectCtx.edit({
              selectedTemplate: id.toString()
            });
            const template = projectCtx.getTemplate(id.toString());
            setSelectedTemplate(template);
            projectCtx.edit({
              project: {
                instructions_html: template.instructions_html,
                turn_around_time: template.turn_around_time,
                requested_completion_date: DateTime.now().plus({
                  days: Number(template.turn_around_time)
                }).toFormat('yyyy-MM-dd')
              }
            });
            setCreationFailed(false);
          }} clearable={false} />
          </div>

          {template && template?.scopes && <div className={css({
          marginTop: '28px'
        })}>
              <Accordion onChange={({
            expanded
          }) => console.log(expanded)} accordion overrides={{
            PanelContainer: {
              style: ({
                $theme
              }) => ({
                outline: `1px solid ${$theme.colors.borderOpaque}`,
                borderRadius: '4px',
                borderBottom: 'none',
                marginBottom: '24px'
              })
            }
          }}>
                <Panel key={template.id} title={`Selected Template Scope Items (${template.scopes.length})`}>
                  {template.scopes.map(scope => <p>{scope.name}</p>)}
                </Panel>
              </Accordion>
            </div>}
        </div>

        <div className={css({
        marginBottom: '1em'
      })} data-testid="project-name-input">
          <FormHeader level={4} required>
            Project Name
          </FormHeader>
          <Input value={projectCtx.getProject().name ?? ''} onChange={e => {
          projectCtx.edit({
            project: {
              name: e.target.value
            }
          });
        }} required />
        </div>

        <div className={css({
        marginBottom: '1em'
      })}>
          <FormHeader level={4} required>
            Instructions
          </FormHeader>
          <Editor value={projectCtx.getInstructionsHtml()} onChange={change => {
          projectCtx.edit({
            project: {
              instructions_html: change
            }
          });
        }} />
        </div>

        <div className={css({
        marginBottom: '1em'
      })}>
          <FormHeader level={4} required>
            What is the expected turn around time for this Project? (days from
            activation date)
          </FormHeader>
          <Input type="number" value={projectCtx.getTurnAroundTime()} required endEnhancer="days" onChange={e => {
          setCreationFailed(false);
          projectCtx.edit({
            project: {
              requested_completion_date: DateTime.now().plus({
                days: Number(e.target.value)
              }).toFormat('yyyy-MM-dd'),
              turn_around_time: Number(e.target.value)
            }
          });
        }} />
        </div>

        <Heading styleLevel={4}>Subjects</Heading>
        <Subjects />

        <ButtonRow flexPosition="flex-end">
          <Button disabled={!projectCtx.getProject().name || !projectCtx.getTurnAroundTime()} kind={creationFailed ? 'error' : 'success'} loading={isCreating} onClick={() => setIsModalOpen(true)}>
            {creationFailed ? 'Failed to Create Project' : 'Create Project'}
          </Button>
        </ButtonRow>
      </HeadingLevel>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} animate autoFocus role={ROLE.alertdialog} size={SIZE.default} overrides={{
      Root: {
        style: ({
          $theme
        }) => ({
          margin: '64px 0 0 0'
        })
      }
    }}>
        <ModalHeader>Create Project</ModalHeader>
        <ModalBody>
          <p>You are about to create a Project with the below details:</p>
          <p>
            <b>Project Name:</b> {projectCtx.getProject().name}
          </p>
          <p>
            <b>Due Date:</b>{' '}
            {formatDate(projectCtx.getProject().requested_completion_date)}
          </p>
          {selectedTemplate && <div>
              <p>
                <b>Template:</b> {selectedTemplate.name}
                {selectedTemplate.name.slice(0, 3) === 'GTI' && <p className={css({
              backgroundColor: 'rgba(255, 186, 0, 0.1)',
              borderRadius: '4px',
              color: 'rgb(255, 186, 0)',
              fontWeight: 'bold',
              marginTop: '14px',
              padding: '14px'
            })}>
                    This is a GTI template so only <i>online</i> checks will be
                    performed
                  </p>}
              </p>
              <p>
                <b>Scopes Items:</b>{' '}
              </p>
              <ul>
                {selectedTemplate.scopes.map(s => <li key={nanoid()}>{s.name}</li>)}
              </ul>

              {projectCtx.subjects() && projectCtx.subjects().length > 0 && <React.Fragment>
                  <p>
                    <b>Subjects:</b>{' '}
                  </p>
                  <ul>
                    {projectCtx.subjects().map(s => <li key={nanoid()} className={css({
                marginBottom: '14px'
              })}>
                        <ul>
                          <b>Name:</b> {s.name}
                          <li>
                            <b>Jurisdictions:</b>{' '}
                            {mapJurisdictions(s.jurisdictions).join(', ')}
                          </li>
                          <li>
                            <b>Description:</b> {s.description}
                          </li>
                          <li>
                            <b>Type:</b> {SubjectTypes[s.subject_type]}
                          </li>
                        </ul>
                      </li>)}
                  </ul>
                </React.Fragment>}
            </div>}
        </ModalBody>
        <ModalFooter>
          <ButtonRow flexPosition="flex-end">
            <Button kind={creationFailed ? 'error' : 'success'} loading={isCreating} disabled={creationFailed} onClick={() => {
            setIsCreating(true);
            projectCtx.create().then(project => {
              if (project) {
                toaster.positive('Project created successfully');
                setProjectCreated(true);
                setIsModalOpen(false);
              } else {
                toaster.negative('An error occurred when creating the project. Contact support');
              }
            }).catch(e => {
              setProjectCreated(false);
              setCreationFailed(true);
              console.error(e);
              toaster.negative('An error occurred when creating the project. Contact support');
            });
            setIsCreating(false);
          }}>
              {creationFailed ? 'Failed' : 'Create'}
            </Button>
            <Button loading={isCreating} onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
          </ButtonRow>
        </ModalFooter>
      </Modal>
    </section>;
};
export default ProjectCreationForm;